import React, { useState, useEffect, useRef } from 'react';
import { ChatButton } from './ChatButton';
import { ChatWindow } from './ChatWindow';
import { ChatForm } from './ChatForm';
import { chatStorage } from '../../utils/chatStorage';
import { useTranslation } from '../../utils/i18n';
import { apiClient } from '../../utils/apiClient';

export function ChatModule() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const eventSourceRef = useRef(null);
  const currentMessageRef = useRef('');
  const streamingMessageIdRef = useRef(null);

  useEffect(() => {
    const savedSession = chatStorage.getSession();
    if (savedSession && savedSession.status === 'active') {
      setSession(savedSession);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const eventSource = eventSourceRef.current;
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []);

  const connectToSSE = async (sessionId, message) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setIsConnected(true);
    setIsTyping(true);
    setError(null);

    try {
      const response = await apiClient.sendChatMessage(sessionId, message);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      currentMessageRef.current = '';

      const messageId = Date.now();
      streamingMessageIdRef.current = messageId;

      setMessages(prev => [...prev, {
        id: messageId,
        sender_type: 'support',
        message: '',
        created_at: new Date().toISOString(),
        isStreaming: true
      }]);

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          setIsTyping(false);
          setMessages(prev => prev.map(msg =>
            msg.id === streamingMessageIdRef.current
              ? { ...msg, isStreaming: false }
              : msg
          ));
          streamingMessageIdRef.current = null;
          currentMessageRef.current = '';
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim()) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.substring(6);
              try {
                const data = JSON.parse(jsonStr);
                if (data.content) {
                  currentMessageRef.current += data.content;
                  setMessages(prev => prev.map(msg =>
                    msg.id === streamingMessageIdRef.current
                      ? { ...msg, message: currentMessageRef.current }
                      : msg
                  ));
                }
              } catch (err) {
                console.error('Error parsing SSE data:', err);
              }
            } else {
              try {
                const data = JSON.parse(line);
                if (data.content) {
                  currentMessageRef.current += data.content;
                  setMessages(prev => prev.map(msg =>
                    msg.id === streamingMessageIdRef.current
                      ? { ...msg, message: currentMessageRef.current }
                      : msg
                  ));
                }
              } catch (err) {
                console.error('Error parsing chunk:', err);
              }
            }
          }
        }
      }

      setIsConnected(false);
    } catch (err) {
      setIsConnected(false);
      setIsTyping(false);
      if (streamingMessageIdRef.current) {
        setMessages(prev => prev.filter(msg => msg.id !== streamingMessageIdRef.current));
        streamingMessageIdRef.current = null;
      }
      setError(t('chat.connection_error'));
      console.error('SSE Error:', err);
    }
  };

  const handleCreateSession = async (formData) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiClient.createChatSession(formData);

      if (!data.success) {
        throw new Error(data.error || t('chat.session_error'));
      }

      const sessionData = {
        id: data.session_id,
        name: formData.name,
        email: formData.email,
        contact: formData.contact,
        status: 'active',
      };

      chatStorage.saveSession(sessionData);
      setSession(sessionData);

      setMessages([{
        id: Date.now(),
        sender_type: 'support',
        message: t('chat.welcome_message'),
        created_at: new Date().toISOString()
      }]);
    } catch (err) {
      setError(err.message || t('chat.session_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (message) => {
    if (!session) return;

    setError(null);

    setMessages(prev => [...prev, {
      id: Date.now(),
      sender_type: 'user',
      message: message,
      created_at: new Date().toISOString()
    }]);

    try {
      await connectToSSE(session.id, message);
    } catch (err) {
      setError(err.message || t('chat.send_error'));
    }
  };

  const handleCloseChat = () => {
    setIsOpen(false);
  };

  return (
    <>
      <ChatButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} />

      {isOpen && (
        <div className="fixed inset-0 z-40 md:relative md:inset-auto">
          {!session ? (
            <div className="fixed bottom-6 right-6 z-50 w-[380px] rounded-2xl shadow-2xl bg-white dark:bg-blue-950 border border-indigo-200 dark:border-blue-800">
              <ChatForm
                onSubmit={handleCreateSession}
                isLoading={isLoading}
                error={error}
              />
            </div>
          ) : (
            <ChatWindow
              session={session}
              onClose={handleCloseChat}
              onSendMessage={handleSendMessage}
              messages={messages}
              isConnected={isConnected}
              error={error}
              isTyping={isTyping}
            />
          )}
        </div>
      )}
    </>
  );
}
